/* eslint-disable no-extend-native */
/**
 * Guards against the `NotFoundError: Failed to execute 'removeChild' / 'insertBefore'
 * on 'Node'` crash that browser translation extensions (Google Translate, the built-in
 * browser translator, etc.) trigger in React apps.
 *
 * When the translator swaps a text node for a translated one, React's reconciler later
 * tries to remove or reorder a node the extension has already replaced, and the raw DOM
 * call throws. Making these operations fail safe when the target is no longer a child of
 * the expected parent keeps the app from crashing while translation is on.
 *
 * Ref: https://github.com/facebook/react/issues/11538
 */
export function installBrowserTranslationGuard(): void {
  if (typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    child: T
  ): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    node: T,
    child: Node | null
  ): T {
    if (child && child.parentNode !== this) {
      return node;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };
}
